// MorrowOS upgrade / paywall modal — a singleton store any code can trigger when
// the server (or a client-side entitlement check) blocks a gated action. Mirrors
// the confirmDialog.ts store pattern: a module-level state + listener set read
// through useSyncExternalStore by <UpgradeModalHost/> (mounted once in __root).
//
//   import { openUpgradeModal } from "./upgradeModal";
//   openUpgradeModal({ capability: "model.tier.pay", requiredTier: "pay" });
//
// The host renders the dialog and owns navigation to the pricing page.

/**
 * Structured reason a gate blocked — the client mirror of the server's
 * `UpgradeReason` (billing/metering.ts). Carries the context, the concrete
 * used/limit numbers, and which call-to-action clears the wall, so the modal can
 * pick precise copy + the right CTA without a second fetch. Optional everywhere:
 * a legacy string-marker gate yields only `{ capability }`.
 */
export interface UpgradeReason {
  readonly capability: string;
  readonly context?: "chat" | "agent" | "workflow" | "browser" | "create" | "connect" | "invite";
  readonly used?: number;
  readonly limit?: number;
  readonly unit?: "usd" | "count" | "minutes" | "seconds" | "mb";
  readonly action?: "topup" | "upgrade" | "addon" | "unlock";
  readonly nextTier?: string;
  readonly addonSku?: string;
}

export interface UpgradeModalRequest {
  /** "upsell" (default) = the paywall pitch; "success" = the post-checkout
   * celebration ("you set me free"). */
  readonly mode?: "upsell" | "success";
  /** The capability that blocked, if known (drives the default copy). */
  readonly capability?: string | null;
  /** The tier the user must reach to unblock, if known. */
  readonly requiredTier?: string | null;
  /** An explicit message overriding the derived copy. */
  readonly message?: string | null;
  /** The plan just unlocked, for the success celebration copy. */
  readonly planName?: string | null;
  /** The full structured reason, when the gate supplied one (drives contextual
   * copy + the correct CTA). `capability` above is derived from it when present. */
  readonly reason?: UpgradeReason | null;
}

type Normalized = {
  readonly mode: "upsell" | "success";
  readonly capability: string | null;
  readonly requiredTier: string | null;
  readonly message: string | null;
  readonly planName: string | null;
  readonly reason: UpgradeReason | null;
};

export type UpgradeModalState =
  | { readonly status: "closed" }
  | { readonly status: "open"; readonly request: Normalized }
  | { readonly status: "closing"; readonly request: Normalized };

const closedState: UpgradeModalState = { status: "closed" };
let state: UpgradeModalState = closedState;
let registeredHostCount = 0;
const listeners = new Set<() => void>();

function publish(next: UpgradeModalState): void {
  state = next;
  for (const listener of listeners) listener();
}

const normalize = (request: UpgradeModalRequest): Normalized => ({
  mode: request.mode ?? "upsell",
  // A structured reason's capability wins over a bare `capability` field.
  capability: request.reason?.capability ?? request.capability ?? null,
  requiredTier: request.requiredTier ?? request.reason?.nextTier ?? null,
  message: request.message ?? null,
  planName: request.planName ?? null,
  reason: request.reason ?? null,
});

export function readUpgradeModalState(): UpgradeModalState {
  return state;
}

export function subscribeUpgradeModal(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Register the renderer host. Unmounting the last host force-closes the modal. */
export function registerUpgradeModalHost(): () => void {
  registeredHostCount += 1;
  let registered = true;
  return () => {
    if (!registered) return;
    registered = false;
    registeredHostCount = Math.max(0, registeredHostCount - 1);
    if (registeredHostCount === 0) publish(closedState);
  };
}

/** Open the upgrade modal. No-op when no host is mounted (nothing would show). */
export function openUpgradeModal(request: UpgradeModalRequest = {}): void {
  if (registeredHostCount === 0) return;
  publish({ status: "open", request: normalize(request) });
}

/** Open the post-checkout celebration ("you set me free"). Same host, success mode. */
export function openUpgradeSuccessModal(
  opts: { readonly planName?: string | null; readonly message?: string | null } = {},
): void {
  openUpgradeModal({
    mode: "success",
    planName: opts.planName ?? null,
    message: opts.message ?? null,
  });
}

/** Begin the close animation; the host calls `completeUpgradeModalClose` when done. */
export function closeUpgradeModal(): void {
  if (state.status !== "open") return;
  publish({ status: "closing", request: state.request });
}

export function completeUpgradeModalClose(): void {
  if (state.status !== "closing") return;
  publish(closedState);
}

export function resetUpgradeModalForTests(): void {
  registeredHostCount = 0;
  publish(closedState);
  listeners.clear();
}

/**
 * A blocked agent turn reaches the client as a plain error string, with the gate
 * smuggled into it by the server. Two marker forms are understood:
 *
 *   - Structured (preferred): `⟦upgrade2:<base64url-json>⟧<human message>` — the
 *     payload decodes to a full `UpgradeReason` (see server `encodeGateReason`).
 *   - Legacy: `⟦upgrade:<capability>⟧<human message>` (server `encodeGateMessage`).
 *
 * Either way we return `{ capability, message }` (so every existing caller keeps
 * working) plus `reason`, which is the full object for the structured form and
 * `null` for the legacy form. Returns null for a normal, non-gate error.
 */
const GATE_REASON_RE = /^⟦upgrade2:([^⟧]+)⟧([\s\S]*)$/;
const GATE_UPGRADE_RE = /^⟦upgrade:([^⟧]+)⟧([\s\S]*)$/;

/** Decode base64url ASCII JSON back into an `UpgradeReason`, or null if malformed. */
function decodeReason(payload: string): UpgradeReason | null {
  try {
    const pad = payload.length % 4 === 0 ? "" : "=".repeat(4 - (payload.length % 4));
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/") + pad);
    const obj = JSON.parse(json) as UpgradeReason;
    if (obj && typeof obj.capability === "string" && obj.capability.length > 0) return obj;
    return null;
  } catch {
    return null;
  }
}

/**
 * If `res` is a 402 plan gate, read its encoded marker and pop the upgrade modal,
 * returning true (handled). Otherwise returns false so the caller proceeds. Use at
 * any gated WRITE fetch site: `if (await maybeOpenUpgradeFrom402(res)) return;`.
 * Clones the response so the caller can still read the body when it's NOT a 402.
 */
export async function maybeOpenUpgradeFrom402(res: Response): Promise<boolean> {
  if (res.status !== 402) return false;
  const body = (await res
    .clone()
    .json()
    .catch(() => null)) as { error?: string } | null;
  const gate = parseGateUpgrade(body?.error);
  if (gate) {
    openUpgradeModal({ capability: gate.capability, message: gate.message, reason: gate.reason });
  }
  return true;
}

export function parseGateUpgrade(
  text: string | null | undefined,
): { capability: string; message: string; reason: UpgradeReason | null } | null {
  if (!text) return null;
  const structured = GATE_REASON_RE.exec(text);
  if (structured) {
    const reason = decodeReason(structured[1]!);
    // A malformed structured payload still shouldn't swallow the human message.
    if (reason) return { capability: reason.capability, message: structured[2]!.trim(), reason };
    return { capability: "", message: structured[2]!.trim(), reason: null };
  }
  const legacy = GATE_UPGRADE_RE.exec(text);
  if (!legacy) return null;
  return { capability: legacy[1]!, message: legacy[2]!.trim(), reason: null };
}
